import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const POLL_MS = 2000;
const MAX_POLLS = 30;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | done | error | timeout
  const [result, setResult] = useState(null);
  const [kind, setKind] = useState(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let active = true;
    const tick = async () => {
      if (!active) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setKind(data.kind);
          setResult(data.result || {});
          setState("done");
          return;
        }
      } catch { /* keep polling */ }
      pollsRef.current += 1;
      if (pollsRef.current >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] rounded-full bg-gold/20 blur-[120px]" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-10 max-w-md text-center relative z-10" data-testid="payment-success-card">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-gold mx-auto mb-5 animate-spin" />
            <h1 className="font-display font-black text-3xl mb-2">Confirming your payment…</h1>
            <p className="text-foreground/60">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "done" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-forest mx-auto mb-5" />
            <h1 className="font-display font-black text-3xl mb-2">{kind === "extend" ? "Room extended!" : kind === "animation" ? "Style unlocked!" : "Payment complete!"}</h1>
            <p className="text-foreground/60 mb-8">{kind === "extend" ? "Your reveal room now has 5 more minutes. Return to the tab where your call is open — the timer updates automatically. You can close this tab." : kind === "animation" ? "Your premium reveal animation is ready." : "Your reveal room is ready to go."}</p>
            <div className="flex gap-3 justify-center flex-wrap">
              {kind === "extend" ? (
                <Button data-testid="close-tab-btn" onClick={() => { window.close(); navigate("/dashboard"); }} className="rounded-full h-12 px-6 bg-gold hover:bg-gold/90 text-black font-semibold">Done — return to your call</Button>
              ) : result?.event_id ? (
                <Button data-testid="view-event-btn" onClick={() => navigate(`/event/${result.event_id}`)} className="rounded-full h-12 px-6 bg-gold hover:bg-gold/90 text-black font-semibold">{kind === "animation" ? "Back to my reveal" : "Open my reveal"}</Button>
              ) : (
                <Button onClick={() => navigate("/dashboard")} className="rounded-full h-12 px-6 bg-gold hover:bg-gold/90 text-black font-semibold">Go to dashboard</Button>
              )}
              <Button variant="outline" onClick={() => navigate("/dashboard")} className="rounded-full h-12 px-6 border-white/20 bg-transparent">Dashboard</Button>
            </div>
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <XCircle className="w-12 h-12 text-destructive mx-auto mb-5" />
            <h1 className="font-display font-black text-3xl mb-2">{state === "timeout" ? "Still processing…" : "Something went wrong"}</h1>
            <p className="text-foreground/60 mb-8">{state === "timeout" ? "Your payment may still be completing. Check your dashboard shortly." : "We couldn't confirm this payment."}</p>
            <Button onClick={() => navigate("/dashboard")} className="rounded-full h-12 px-6 bg-gold hover:bg-gold/90 text-black font-semibold">Go to dashboard</Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
